// The component set of docs/08-app-design.md section 3, in the grammar of
// design/vault.css and design/app.css. Every component is a plain function
// over that class vocabulary; none of them fetches, stores or signs
// anything. Numbers are always rendered from raw units so that what the
// user sees is what the program will enforce.

import qrcode from 'qrcode-generator';
import { type ButtonHTMLAttributes, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

// ---------------------------------------------------------------- amounts

/** Format `raw` units of a `decimals`-place token for display, never rounding a unit away silently. */
export function formatUnits(raw: bigint, decimals: number, maxFraction = decimals): { text: string; truncated: boolean } {
  const negative = raw < 0n;
  const abs = negative ? -raw : raw;
  const base = 10n ** BigInt(decimals);
  const whole = abs / base;
  const frac = abs % base;
  let fracText = frac.toString().padStart(decimals, '0');
  let truncated = false;
  if (maxFraction < decimals) {
    const dropped = fracText.slice(maxFraction);
    truncated = /[1-9]/.test(dropped);
    fracText = fracText.slice(0, maxFraction);
  }
  fracText = fracText.replace(/0+$/, '');
  const wholeText = whole.toLocaleString('en-US');
  return { text: `${negative ? '-' : ''}${wholeText}${fracText ? '.' + fracText : ''}`, truncated };
}

export interface AmountProps {
  raw: bigint;
  decimals: number;
  symbol: string;
  /** Digits shown after the point; the raw value is always available on tap. */
  maxFraction?: number;
  /** Indicative fiat hint (docs/08 section 11, decided: from the Jupiter quote). */
  hint?: string;
  size?: 'body' | 'large' | 'hero';
}

/** A token amount with tabular figures; a small "+" marks a display that drops non-zero units, and the exact raw value is on tap. */
export function Amount({ raw, decimals, symbol, maxFraction, hint, size = 'body' }: AmountProps) {
  const [exact, setExact] = useState(false);
  const { text, truncated } = formatUnits(raw, decimals, maxFraction ?? Math.min(decimals, 6));
  return (
    <span className={`amount ${size === 'body' ? '' : size}`.trim()} onClick={() => setExact((v) => !v)} title={`${raw.toString()} raw units`}>
      {exact ? raw.toString() : text}
      <span className="symbol">{exact ? 'raw' : symbol}</span>
      {!exact && truncated ? (
        <span className="symbol more" title="A little more than shown: tap for the exact amount">
          +<span className="sr-only"> a little more</span>
        </span>
      ) : null}
      {hint ? <span className="hint">{hint}, indicative</span> : null}
    </span>
  );
}

// -------------------------------------------------------------- addresses

// 'older fee reserve': a legacy device's per-device key, labelled apart from the top-up destination (docs/18 section 6).
export type AddressKind = 'vault' | 'treasury' | 'fee reserve' | 'older fee reserve' | 'wallet' | 'program' | 'token account' | 'mint';

export interface AddressProps {
  value: string;
  kind?: AddressKind;
  /** Explorer link base; the address is appended. Omitted → no link. */
  explorer?: string;
  full?: boolean;
  onCopied?: () => void;
}

export function shortAddress(value: string): string {
  return `${value.slice(0, 4)}…${value.slice(-4)}`;
}

/** Short form by default, full on tap, copy button, explorer link, kind badge. */
export function Address({ value, kind, explorer, full: fullDefault, onCopied }: AddressProps) {
  const [full, setFull] = useState(!!fullDefault);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      onCopied?.();
    } catch {
      /* clipboard refused: the full form is still selectable */
    }
  };
  return (
    <span className="address">
      {kind ? <span className="kind">{kind}</span> : null}
      <span className="value" onClick={() => setFull((v) => !v)} title={value}>
        {full ? value : shortAddress(value)}
      </span>
      <button type="button" aria-label="Copy address" onClick={copy}>
        copy
      </button>
      {explorer ? (
        <a href={`${explorer}${value}`} target="_blank" rel="noreferrer noopener">
          explorer ↗
        </a>
      ) : null}
    </span>
  );
}

// ---------------------------------------------------------------- figures

/** One number with its source line ("from getTransaction", "from the quote at slot N"). */
export function Figure({ children, source }: { children: ReactNode; source: string }) {
  return (
    <span className="figure">
      <span>{children}</span>
      <span className="source">{source}</span>
    </span>
  );
}

// ------------------------------------------------------------- notice, kv

export function Notice({ label, tone, children }: { label?: string; tone?: 'accent' | 'warn' | 'fail' | 'inverted'; children: ReactNode }) {
  return (
    <section className={`notice ${tone ?? ''}`.trim()}>
      {label ? <span className="eyebrow">{label}</span> : null}
      {children}
    </section>
  );
}

export function Kv({ rows }: { rows: Array<[ReactNode, ReactNode]> }) {
  return (
    <dl className="kv">
      {rows.map(([k, v], i) => (
        <div key={i}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

// ------------------------------------------------------------ status line

export type State = 'landed' | 'pending' | 'failed' | 'neutral';

/** The only way state is shown: a colour, a word and one sentence; never colour alone. */
export function StatusLine({ state, word, id, children }: { state: State; word: string; /** For a control that points at it with aria-describedby. */ id?: string; children: ReactNode }) {
  return (
    <div className="status-line" data-state={state} role="status" id={id}>
      <span className="square" aria-hidden="true" />
      <span>
        {/* the space keeps the word apart from the sentence for assistive technology ("unlocked. That", not "unlocked.That") */}
        <span className="word">{word}</span> {children}
      </span>
    </div>
  );
}

// -------------------------------------------------------------- countdown

/**
 * Seconds until `validUntilSlot`, from the last observed slot and a measured
 * slot rate. Re-renders every second; never pretends to know the chain
 * better than its inputs (the label says "about").
 */
export function Countdown({ validUntilSlot, observedSlot, observedAt, msPerSlot }: { validUntilSlot: bigint; observedSlot: bigint; observedAt: number; msPerSlot: number }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const elapsedSlots = BigInt(Math.floor((now - observedAt) / msPerSlot));
  const remaining = validUntilSlot - observedSlot - elapsedSlots;
  const seconds = Math.max(0, Math.round((Number(remaining) * msPerSlot) / 1000));
  return (
    <span className="countdown" data-urgent={seconds <= 15 ? 'true' : 'false'}>
      {seconds === 0 ? 'expired' : `about ${seconds} s`}
    </span>
  );
}

// ---------------------------------------------------------------- stepper

export interface Step {
  title: string;
  detail?: ReactNode;
  state: 'todo' | 'active' | 'done' | 'failed';
}

export function Stepper({ steps }: { steps: Step[] }) {
  return (
    <ol className="stepper">
      {steps.map((s, i) => (
        <li key={i} className="step" data-state={s.state}>
          <span className="num" aria-hidden="true">
            {s.state === 'done' ? '■' : String(i + 1).padStart(2, '0')}
          </span>
          <span>
            <span className="title">{s.title}</span>
            {s.detail ? <span className="detail">{s.detail}</span> : null}
          </span>
        </li>
      ))}
    </ol>
  );
}

// ------------------------------------------------------------ key ruler

/** A straight 256-tick ruler of one tree: used leaves, the free ones, and leaf 255 (the refresh key) in the accent. */
export function KeyRuler({ usedLeaves, label }: { usedLeaves: Uint8Array; label: string }) {
  const ticks = useMemo(() => Array.from({ length: 256 }, (_, i) => ((usedLeaves[i >> 3] ?? 0) & (1 << (i & 7))) !== 0), [usedLeaves]);
  return (
    <div className="key-ruler live" role="img" aria-label={label}>
      {ticks.map((used, i) => (
        <i key={i} className={used ? 'used' : i === 255 ? 'recovery' : 'free'} />
      ))}
    </div>
  );
}

// ------------------------------------------------------------ sheet, toast

export function Sheet({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);
  return (
    <dialog ref={ref} className="sheet" aria-label={title} onClose={onClose} onCancel={onClose}>
      <div className="dialog-top">
        <span>{title}</span>
        <button type="button" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </div>
      <div className="dialog-body">{open ? children : null}</div>
    </dialog>
  );
}

/** Only for "copied" and "locked"; never for anything the user must act on. */
export function Toast({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="toast" role="status">
      {message}
    </div>
  );
}

/** A toast that clears itself; returns the setter. */
export function useToast(): [string | null, (m: string) => void] {
  const [message, setMessage] = useState<string | null>(null);
  useEffect(() => {
    if (!message) return;
    const id = setTimeout(() => setMessage(null), 1800);
    return () => clearTimeout(id);
  }, [message]);
  return [message, setMessage];
}

// ---------------------------------------------------------------- buttons

export function Button({ variant = 'primary', block, small, working, children, className, ...rest }: { variant?: 'primary' | 'ghost' | 'danger'; block?: boolean; small?: boolean; working?: boolean } & ButtonHTMLAttributes<HTMLButtonElement>) {
  const cls = ['button', variant === 'primary' ? '' : variant, block ? 'block' : '', small ? 'small' : '', working ? 'working' : '', className ?? ''].filter(Boolean).join(' ');
  return (
    <button type="button" {...rest} className={cls}>
      {children}
    </button>
  );
}

// -------------------------------------------------------------------- QR

/** A QR code of `text` as inline SVG, black on white so any scanner reads it. */
export function Qr({ text, label }: { text: string; label: string }) {
  const svg = useMemo(() => {
    const qr = qrcode(0, 'M');
    qr.addData(text);
    qr.make();
    return qr.createSvgTag({ cellSize: 4, margin: 0, scalable: true });
  }, [text]);
  return <div className="qr" role="img" aria-label={label} dangerouslySetInnerHTML={{ __html: svg }} />;
}

// ---------------------------------------------------------------- fields

export function Field({ id, label, help, children }: { id: string; label: string; help?: ReactNode; children: ReactNode }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children}
      {/* a field points at its help with aria-describedby={`${id}-help`} */}
      {help ? (
        <span className="help" id={`${id}-help`}>
          {help}
        </span>
      ) : null}
    </div>
  );
}
